/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery } from "@mui/material";
import Popup from "../../components/popups/popup";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CityDropdown from "../../components/dropdowns/cityDropdown";
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from "../../components/inputs/onOffSwitchForDeleted";
import CityPriceService from "../../services/cityPriceService";
import BranchDropdown from "../../components/dropdowns/branchDropdown";
import UpdateCityPriceBox from "../../components/boxes/cityPrice/updateCityPriceBox";
import AddCityPriceBox from "../../components/boxes/cityPrice/addCityPriceBox";
import DeleteCityPriceModal from "../../components/modals/cityPrice/deleteCityPriceModal";
import { formatPrice } from "../../utils";

const CityPrices = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [cityPrices, setCityPrices] = useState([]);
    const [selectedCityPrice, setSelectedCityPrice] = useState({});

    const [openUpdate, setOpenUpdate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleReload = async () => {
        setOpenUpdate(false);
        setHasMore(true);
        setPage(1);
        setSelectedCity(null);
        setSelectedBranch(null);
        await fetchCityPrices();

    };

    const handleOpenUpdate = (cityPrice) => {
        setOpenUpdate(true);
        setSelectedCityPrice(cityPrice);
    };

    const handleOpenDelete = (cityPrice) => {
        setSelectedCityPrice(cityPrice);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSelectedCity(null);
            setSelectedBranch(null);
            await fetchCityPrices();
        }
    };


    const fetchCityPrices = async (page = 1, cityId = null, branchId = null) => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = { cityId, branchId, ...sortState, deleted };
            const data = await CityPriceService.fetch(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setCityPrices([]);
                }
            } else {
                setCityPrices((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching streets:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetFilter = () => {
        setHasMore(true);
        setSelectedCity(null);
        setSelectedBranch(null);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchCityPrices(page, selectedCity?.id, selectedBranch?.id);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchCityPrices(1, selectedCity?.id, selectedBranch?.id);
    }, [selectedCity, selectedBranch, sortState, deleted]);


    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchCityPrices(page + 1, selectedCity?.id, selectedBranch?.id);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteCityPriceModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} cityPrice={selectedCityPrice} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}


            {isTablet && <Box>
                {
                    (selectedCityPrice && openUpdate) ?
                        <UpdateCityPriceBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} cityPrice={selectedCityPrice} />
                        :
                        <AddCityPriceBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                            setPopupType={setPopupType}
                            setShowPopup={setShowPopup} />
                }
            </Box>}

            {/* Filter row */}
            {
                isMobile ?
                    <Box
                        sx={{
                            my: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                        }}>
                            <Box sx={{ px: 2, }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'90%'}>
                                <CityDropdown
                                    isFilter={true}
                                    selectedCity={selectedCity}
                                    setSelectedCity={setSelectedCity}
                                />
                            </Box>
                        </Box>

                        <Divider />
                        <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '100%'
                        }}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                        <Divider />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50,
                        }}>
                            <Box sx={{
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            my: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2, cursor: 'pointer' }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'34%'}>
                            <CityDropdown
                                isFilter={true}
                                selectedCity={selectedCity}
                                setSelectedCity={setSelectedCity}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'34%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }



            <Box sx={{
                display: 'flex'
            }}>
                {!isTablet && <Box sx={{
                    width: '35%'
                }}>
                    {
                        (selectedCityPrice && openUpdate) ?
                            <UpdateCityPriceBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} cityPrice={selectedCityPrice} />
                            :
                            <AddCityPriceBox handleReload={handleReload} setPopupMsg={setPopupMsg}
                                setPopupType={setPopupType}
                                setShowPopup={setShowPopup} />
                    }
                </Box>}

                {!isTablet && <Box width={10}></Box>}

                {/* Scrollable Table with Sticky Header */}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={handleScroll}
                    sx={{
                        boxShadow: 'none',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        borderRadius: 2,
                        height: 'calc(100vh - 170px)',
                        overflowY: 'auto',
                        width: isTablet ? '100%' : '65%'
                    }}
                >
                    <Table stickyHeader aria-label="simple table">
                        <TableHead
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}
                        >
                            <TableRow>
                                <TableCell align={isEn ? 'left' : 'right'}
                                    onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('id')}
                                        {sortState.sortBy === 'id' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('city')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('city')}
                                        {sortState.sortBy === 'city' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('branch')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('branch')}
                                        {sortState.sortBy === 'branch' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                    onClick={() => onSort('transportationPrice')}>
                                    <Box sx={{ display: 'flex' }} >
                                        {t('transportationPriceSmall')}
                                        {sortState.sortBy === 'transportationPrice' && sortIcon()}
                                    </Box>
                                </TableCell>

                                <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                    {t('actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cityPrices.map((cityPrice, index) => (
                                <TableRow
                                    key={cityPrice.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{cityPrice.id}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{cityPrice.city.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{cityPrice.branch.name}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(cityPrice.transportationPrice)}</TableCell>
                                    <TableCell align={isEn ? 'left' : 'right'}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            border: 1,
                                            borderColor: theme.palette.customGrey.borderInput,
                                            backgroundColor: theme.palette.secondary.main,
                                            borderRadius: 1,
                                        }}>
                                            {cityPrice.deletedAt === null && <Box sx={{
                                                px: 1.5,
                                                pt: 0.4,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenUpdate(cityPrice)}>
                                                <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.text.primary }} />
                                            </Box>}
                                            {cityPrice.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                            <Box sx={{
                                                px: 1.5,
                                                pt: 0.45,
                                                pb: 0.25,
                                                cursor: 'pointer'
                                            }} onClick={() => handleOpenDelete(cityPrice)}>
                                                {cityPrice.deletedAt === null ?
                                                    <DeleteOutlineOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.red.bg }} />
                                                    :
                                                    <ReplayOutlinedIcon fontSize='small'
                                                        sx={{ color: theme.palette.green.bg }} />
                                                }
                                            </Box>

                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>

        </Box>
    );
}

export default CityPrices;